import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import { getAuth0Token } from './auth.service';

const client = generateClient();

// Status options definition
export const statusOptions = [
  { id: 'Submitted', label: 'Submitted', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', borderColor: 'border-yellow-200' },
  { id: 'Active Engagement', label: 'Active Engagement', bgColor: 'bg-green-100', textColor: 'text-green-800', borderColor: 'border-green-200' },
  { id: 'Do Not Contact', label: 'Do Not Contact', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-200' },
  { id: 'Assessment Completed', label: 'Assessment Completed', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' },
  { id: 'Assessment InProcess', label: 'Assessment InProcess', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' },
  { id: 'Assessment Scheduled', label: 'Assessment Scheduled', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' },
  { id: 'Accepted Referral', label: 'Accepted Referral', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' },
  { id: 'Attended Intake', label: 'Attended Intake', bgColor: 'bg-blue-100', textColor: 'text-blue-800', borderColor: 'border-blue-200' },
  { id: 'Closed', label: 'Closed', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-200' },
  { id: 'Archive', label: 'Archive', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-200' },
  { id: 'MRT-Tester', label: 'MRT-Tester', bgColor: 'bg-purple-100', textColor: 'text-purple-800', borderColor: 'border-purple-200' },
  { id: 'MRT-Optout', label: 'MRT-Optout', bgColor: 'bg-purple-100', textColor: 'text-purple-800', borderColor: 'border-purple-200' }
];

// Get all submissions from DynamoDB
export const fetchSubmissions = async () => {
  try {
    let allItems = [];
    let nextToken = null;
    
    do {
      const submissionData = await client.graphql({
        query: queries.listSubmissions,
        variables: {
          limit: 1000, // Set a high limit to reduce number of requests
          nextToken: nextToken
        }
      });
      
      const items = submissionData.data.listSubmissions.items;
      allItems = [...allItems, ...items];
      nextToken = submissionData.data.listSubmissions.nextToken;
    } while (nextToken);
    
    return allItems
      .filter(item => item.Status !== 'Archive')
      .map(item => ({
        id: item.id,
        mobilenumber: item.mobilenumber,
        publicId: item.publicId || '0',
        userscore: item.userscore || '0',
        SID: item.SID || '',
        zipcode: item.zipcode || 'No Zipcode',
        submittedDate: item.createdAt,
        status: item.Status || 'Submitted',
        Notes: item.Notes || '',
        age: item.age || '',
        gender: item.gender || '',
        state: item.state || '',
        transgender: item.transgender || 'no',
        householdincome: item.householdincome || '',
        eligible: item.eligible || '',
        needsAlternateTime: item.needsAlternateTime || false,
        alternateTimeRequestedAt: item.alternateTimeRequestedAt || null
      }));
  } catch (error) {
    console.error('[ERROR] Error fetching submissions:', error);
    if (error.response) {
      console.error('[ERROR] GraphQL response error:', JSON.stringify(error.response, null, 2));
    }
    throw error;
  }
};

// Conversation fields used for message status on the dashboard.
// The lastInbound* fields need the updated Conversation schema; until it is
// deployed, the query falls back to the base fields.
const CONVERSATION_SUMMARY_FIELDS = `
        id
        submissionId
        lastMessageTime
        lastMessageContent
        lastMessageDirection
        unreadCount`;

const INBOUND_SUMMARY_FIELDS = `
        lastInboundMessageTime
        lastInboundMessageContent`;

const listConversationSummariesQuery = (fields) => /* GraphQL */ `
  query ListConversationSummaries($limit: Int, $nextToken: String) {
    listConversations(limit: $limit, nextToken: $nextToken) {
      items {${fields}
      }
      nextToken
    }
  }
`;

const listAllConversationSummaries = async (fields) => {
  let items = [];
  let nextToken = null;
  do {
    const result = await client.graphql({
      query: listConversationSummariesQuery(fields),
      variables: { limit: 1000, nextToken }
    });
    items = [...items, ...(result.data.listConversations.items || [])];
    nextToken = result.data.listConversations.nextToken;
  } while (nextToken);
  return items;
};

// Get the most recent conversation for each submission, keyed by submission ID
export const fetchLatestConversationsBySubmission = async () => {
  let conversations;
  try {
    conversations = await listAllConversationSummaries(CONVERSATION_SUMMARY_FIELDS + INBOUND_SUMMARY_FIELDS);
  } catch (error) {
    console.warn('[WARN] Falling back to base conversation fields:', error?.errors?.[0]?.message || error);
    conversations = await listAllConversationSummaries(CONVERSATION_SUMMARY_FIELDS);
  }

  const bySubmission = {};
  conversations.forEach(conversation => {
    if (!conversation?.submissionId) return;
    const existing = bySubmission[conversation.submissionId];
    if (!existing || new Date(conversation.lastMessageTime || 0) > new Date(existing.lastMessageTime || 0)) {
      bySubmission[conversation.submissionId] = conversation;
    }
  });
  return bySubmission;
};

// Get a single submission by ID (mobilenumber)
export const fetchSubmission = async (mobilenumber) => {
  try {
    const submissionData = await client.graphql({
      query: queries.getSubmission,
      variables: { mobilenumber }
    });
    
    const item = submissionData.data.getSubmission;
    
    if (!item) return null;
    
    return {
      id: item.id,
      createdAt: item.createdAt,
      mobilenumber: item.mobilenumber,
      publicId: item.publicId || '0',
      userscore: item.userscore || '0',
      SID: item.SID || '',
      zipcode: item.zipcode || 'No Zipcode',
      submittedDate: item.createdAt,
      status: item.Status || 'Submitted',
      Notes: item.Notes || '',
      age: item.age || '',
      gender: item.gender || '',
      state: item.state || '',
      transgender: item.transgender || 'no',
      householdincome: item.householdincome || '',
      eligible: item.eligible || '',
      needsAlternateTime: item.needsAlternateTime || false,
      alternateTimeRequestedAt: item.alternateTimeRequestedAt || null
    };
  } catch (error) {
    console.error(`Error fetching submission with mobilenumber ${mobilenumber}:`, error);
    throw error;
  }
};

// Get a single submission by ID (mobilenumber)
export const getSubmissionById = async (id) => {
  try {
    const submissionData = await client.graphql({
      query: queries.getSubmission,
      variables: { id }
    });
    
    const item = submissionData.data.getSubmission;
    
    if (!item) return null;
    
    return {
      id: item.id,
      mobilenumber: item.mobilenumber,
    };
  } catch (error) {
    console.error(`Error fetching submission with mobilenumber ${id}:`, error);
    throw error;
  }
};

export const getSubmissionByMobileNumber = async (mobileNumber) => {
  const result = await client.graphql({
    query: queries.submissionsByMobileNumber,
    variables: {
      mobilenumber: mobileNumber,
      limit: 1,
    },
  });

  const items = result?.data?.submissionsByMobileNumber?.items || [];
  return items.length > 0 ? items[0] : null;
};

export const updateStatus = async (id, status, changedBy = "User", reason = null) => {
  try {
    console.log("Updating status with:", { id, status, changedBy });
    
    // Step 1: Get the current submission to capture previous status
    const submissionData = await client.graphql({
      query: queries.getSubmission,
      variables: { id }
    });
    
    const currentSubmission = submissionData.data.getSubmission;
    const previousStatus = currentSubmission?.Status || null;
    
    // Step 2: Only create history record if status is actually changing
    if (previousStatus !== status) {
      try {
        await client.graphql({
          query: mutations.createSubmissionStatusHistory,
          variables: {
            input: {
              submissionId: id,
              previousStatus,
              newStatus: status,
              changedAt: new Date().toISOString(),
              changedBy,
              reason
            }
          }
        });
        console.log("Status history record created:", { previousStatus, newStatus: status });
      } catch (historyError) {
        console.error('Error creating status history record (continuing with status update):', historyError);
      }
    }
    
    // Step 3: Update the submission status
    const result = await client.graphql({
      query: mutations.updateSubmission,
      variables: {
        input: { 
          id: id,
          Status: status,
          updatedAt: new Date().toISOString(),
          updatedBy: changedBy
        },
        condition: null
      }
    });
    return result.data.updateSubmission;
  } catch (error) {
    console.error('Error updating status:', error);
    throw error;
  }
};

// Set or clear the "needs alternate time" flag on a submission
export const setNeedsAlternateTime = async (id, needsAlternateTime, changedBy = 'System') => {
  try {
    console.log('Updating needsAlternateTime with:', { id, needsAlternateTime });
    const result = await client.graphql({
      query: mutations.updateSubmission,
      variables: {
        input: {
          id,
          needsAlternateTime,
          alternateTimeRequestedAt: needsAlternateTime ? new Date().toISOString() : null,
          updatedAt: new Date().toISOString(),
          updatedBy: changedBy
        },
        condition: null
      }
    });
    return result.data.updateSubmission;
  } catch (error) {
    console.error('Error updating needsAlternateTime:', error);
    throw error;
  }
};

export const updateEligible = async (id, eligibleValue) => {
  try {
    console.log("Updating eligible with:", { id, eligibleValue });
    const result = await client.graphql({
      query: mutations.updateSubmission,
      variables: {
        input: { 
          id: id,
          eligible: eligibleValue,
          updatedAt: new Date().toISOString(),
          updatedBy: "User"
        },
        condition: null
      }
    });
    return result.data.updateSubmission;
  } catch (error) {
    console.error('Error updating eligible:', error);
    throw error;
  }
};

export const updateNotes = async (id, notes) => {
  try {
    console.log("Updating notes with:", { id, notes });
    const result = await client.graphql({
      query: mutations.updateSubmission,
      variables: {
        input: { 
          id: id,
          Notes: notes,
          updatedAt: new Date().toISOString(),
          updatedBy: "User"
        },
        condition: null
      }      
    });
    return result.data.updateSubmission;
  } catch (error) {
    console.error('Error updating notes:', error);
    throw error;
  }
};

// Update submission video link
export const updateVideoLink = async (id, videoLink) => {
  try {
    // Note: If there's no videoLink field in your DynamoDB table,
    // you may need to create a new field or store this information elsewhere
    console.log(`Would update video link for ${id} to ${videoLink} if field existed`);
    return true;
  } catch (error) {
    console.error('Error updating video link:', error);
    throw error;
  }
};

// Add a new submission
export const addSubmission = async ({ mobileNumber, SID }) => {
  try {
    const token = await getAuth0Token();
    const apiUrl = `${process.env.REACT_APP_API_GATEWAY_URL}user`;

    const sendwelcomesms = 0; // Set to false to send welcome SMS
    const payload = {
      mobilenumber:mobileNumber,
      SID,
      sendwelcomesms
    };
    console.log('📤 add submission with payload:', JSON.stringify({
      mobilenumber:mobileNumber,
      SID,
      sendwelcomesms
    }, null, 2));

    const response = await fetch(apiUrl, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    console.log(`📡 Request sent to ${apiUrl}. Status:`, response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Response error body:', errorData);
      throw new Error(errorData.error || 'Failed to add submission');
    }

    const result = await response.json();
    console.log('✅ Submission added successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Error Adding Submission:', error);
    throw error;
  }
};


// Delete a submission
export const deleteSubmissionById = async (id) => {
  try {
    const result = await client.graphql({
      query: mutations.deleteSubmission,
      variables: {
        input: { mobilenumber: id }
      }
    });
    return result.data.deleteSubmission;
  } catch (error) {
    console.error('Error deleting submission:', error);
    throw error;
  }
};

// Fetch SID data from external API
export const fetchSIDData = async (sid) => {
  try {
    const apiKey = process.env.REACT_APP_SID_API_KEY;
    const username = process.env.REACT_APP_SID_USERNAME;
    const password = process.env.REACT_APP_SID_PASSWORD;
    const baseUrl = process.env.REACT_APP_SID_API_BASE_URL;
    
    // Check if all required environment variables are set
    if (!apiKey || !username || !password || !baseUrl) {
      throw new Error('Missing required environment variables: REACT_APP_SID_API_KEY, REACT_APP_SID_USERNAME, REACT_APP_SID_PASSWORD, or REACT_APP_SID_API_BASE_URL');
    }
    
    const url = `${baseUrl}/${sid}`;
    
    console.log('SID API Debug Info:', {
      sid,
      apiKey: '***' + apiKey.slice(-4),
      username: '***' + username.slice(-4),
      password: '***' + password.slice(-4),
      url: url
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Api-key': apiKey,
        'Authorization': 'Basic ' + btoa(`${username}:${password}`),
        'Content-Type': 'application/json'
      }
    });

    console.log('SID API Response Status:', response.status);
    console.log('SID API Response Headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('SID API Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    console.log('SID API Success Response:', data);
    return data;
  } catch (error) {
    console.error('Error fetching SID data:', error);
    throw error;
  }
};

// Check if submission has incomplete data
export const hasIncompleteData = (submission) => {
  // Check if essential fields are missing or empty
  const essentialFields = ['age', 'gender', 'zipcode', 'state', 'householdincome'];
  return essentialFields.some(field => !submission[field] || submission[field] === 'N/A' || submission[field] === '');
};

// Update submission with SID data
export const updateSubmissionWithSIDData = async (submissionId, sidData) => {
  try {
    console.log("Updating submission with SID data:", { submissionId, sidData });
    const result = await client.graphql({
      query: mutations.updateSubmission,
      variables: {
        input: { 
          id: submissionId,
          age: sidData.age || '',
          userscore: sidData.userscore || '',
          gender: sidData.gender || '',
          zipcode: sidData.zipcode || '',
          state: sidData.state || '',
          householdincome: sidData.householdincome || '',
          transgender: sidData.transgender || 'no',
          updatedAt: new Date().toISOString(),
          updatedBy: "SID Data Sync"
        },
        condition: null
      }
    });
    return result.data.updateSubmission;
  } catch (error) {
    console.error('Error updating submission with SID data:', error);
    throw error;
  }
};